import { formatNumber, MUTED, SCOPE_HEX, sectionMetaStyle, sectionTitleStyle, SOFT_BORDER, TEXT, tintedBandStyle } from "../shared/styles";
import { getScopeLabel } from "../shared/scopeLabels";

const SCOPE_ORDER = ["Scope 1", "Scope 2", "Scope 3"];
function buildSubScopeGroups(rows) {
  return SCOPE_ORDER.map((scope) => {
    const subScopes = (rows || [])
      .filter((row) => row?.scope?.startsWith(scope) && row.scope !== scope)
      .reduce((items, row) => {
        const value = Number(row.total_emissions || 0);
        const existing = items.find((item) => item.scope === row.scope);

        if (existing) {
          existing.value += value;
        } else {
          items.push({ scope: row.scope, value });
        }

        return items;
      }, [])
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);

    return { scope, subScopes };
  });
}

export default function BalanceSection({ scopeTotals, currentYearRows, isMobile }) {
  const total = Object.values(scopeTotals || {}).reduce((a, b) => a + b, 0);
  const pieData = ["Scope 1", "Scope 2", "Scope 3"]
    .map((scope) => ({ scope, value: scopeTotals?.[scope] || 0 }))
    .filter((item) => item.value > 0)
    .map(({ scope, value }) => ({
      value,
      label: getScopeLabel(scope),
      color: scope === "Scope 1" ? "iris" : scope === "Scope 2" ? "azure" : "turquoise",
      srHint: `${value.toFixed(1)} t CO₂e in ${getScopeLabel(scope)}`
    }));
  const subScopeGroups = buildSubScopeGroups(currentYearRows);

  return (
    <section style={tintedBandStyle}>
      <div style={{ marginBottom: "1rem" }}>
        <h2 className="h2" style={sectionTitleStyle}>Emissions-Performance</h2>
        <p style={sectionMetaStyle}>Scope-Verteilung der aktuellen Netto-Emissionen.</p>
      </div>

      <div style={{ minWidth: 0, background: "#fff", border: `1px solid ${SOFT_BORDER}`, borderRadius: 8, padding: isMobile ? "1rem" : "1.25rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(260px, 0.85fr) minmax(0, 1.15fr)", gap: isMobile ? "1rem" : "1.25rem", alignItems: "center" }}>
          <div style={{ minWidth: 0, display: "grid", placeItems: "center" }}>
            {total > 0 ? (
              <sdx-pie-chart value={total.toFixed(0)} description="t CO₂e" data={JSON.stringify(pieData)}></sdx-pie-chart>
            ) : (
              <p style={{ color: MUTED, fontSize: "0.9rem" }}>Keine Emissionsdaten vorhanden.</p>
            )}
          </div>

          <div style={{ minWidth: 0, display: "grid", gap: "0.75rem" }}>
            {subScopeGroups.map(({ scope, subScopes }) => {
              const scopeTotal = Number(scopeTotals?.[scope] || 0);
              const color = SCOPE_HEX[scope] || TEXT;

              return (
                <div key={scope} style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "0.75rem", paddingBottom: "0.35rem", borderBottom: `1px solid ${SOFT_BORDER}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", minWidth: 0 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 999, background: color, flexShrink: 0 }} />
                      <strong style={{ color: TEXT, fontSize: "0.9rem", lineHeight: 1.25 }}>{getScopeLabel(scope)}</strong>
                    </div>
                    <span style={{ color: TEXT, fontSize: "0.84rem", fontWeight: 700, whiteSpace: "nowrap" }}>{formatNumber(scopeTotal, 1)} t</span>
                  </div>

                  <div style={{ display: "grid", gap: "0.35rem", marginTop: "0.5rem" }}>
                    {subScopes.length > 0 ? (
                      subScopes.map((subScope) => (
                        <div key={subScope.scope} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "0.75rem", alignItems: "baseline", color: MUTED, fontSize: "0.8rem", lineHeight: 1.35 }}>
                          <span style={{ overflowWrap: "anywhere" }}>{getScopeLabel(subScope.scope)}</span>
                          <span style={{ color: MUTED, whiteSpace: "nowrap" }}>
                            {formatNumber(subScope.value, 1)} t
                          </span>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: MUTED, fontSize: "0.8rem" }}>Keine Unterpositionen vorhanden.</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
